<template>
  <div class="atlas-system-root">
    <p class="title is-4">Звёздная система</p>

    <div v-if="loading" class="notification is-light">
      Загрузка…
    </div>

    <div v-else-if="error" class="notification is-warning is-light">
      {{ error }}
    </div>

    <template v-else-if="system">
      <p class="subtitle is-6">
        <strong>{{ system.star.name }}</strong>
        <span v-if="system.galaxy_name" class="has-text-grey">
          · {{ system.galaxy_name }}
        </span>
      </p>

      <div class="canvas-wrapper">
        <canvas ref="canvas" class="solar-canvas" />
        <div class="zoom-controls">
          <button
            type="button"
            class="button is-small is-light"
            title="Увеличить"
            v-on:click="zoomIn"
          >
            +
          </button>
          <button
            type="button"
            class="button is-small is-light"
            title="Уменьшить"
            v-on:click="zoomOut"
          >
            −
          </button>
        </div>
      </div>

      <div class="legend is-size-7">
        <span class="legend-item"><span class="dot star-dot" /> Звезда</span>
        <span class="legend-item"><span class="dot planet-dot" /> Планета</span>
        <span class="legend-item"><span class="dot ship-dot" /> Корабль / станция</span>
        <span class="legend-item"><span class="dot player-dot" /> Вы здесь</span>
      </div>
    </template>
  </div>
</template>

<script>
import AtlasService from '../../services/AtlasService'
import { renderSolarSystem } from '../../lib/solarSystemRenderer'

const ZOOM_STEP = 1.25
const ZOOM_MIN = 0.5
const ZOOM_MAX = 4

export default {
  data () {
    return {
      system: null,
      loading: false,
      error: null,
      resizeObserver: null,
      zoomLevel: 1,
    }
  },
  mounted () {
    this.loadSystem()
  },
  beforeDestroy () {
    if (this.resizeObserver) {
      this.resizeObserver.disconnect()
    }
    window.removeEventListener('resize', this.handleResize)
  },
  methods: {
    async loadSystem () {
      this.loading = true
      this.error = null
      try {
        const resp = await AtlasService.getSystem()
        this.system = resp.data.data || resp.data
        this.$nextTick(() => {
          this.setupResize()
          this.draw()
        })
      } catch (err) {
        const message = err.response?.data?.error ||
          'Не удалось загрузить данные системы'
        this.error = message
      } finally {
        this.loading = false
      }
    },
    draw () {
      const canvas = this.$refs.canvas
      if (!canvas || !this.system) return
      renderSolarSystem(canvas, this.system, { zoom: this.zoomLevel })
    },
    zoomIn () {
      this.zoomLevel = Math.min(ZOOM_MAX, this.zoomLevel * ZOOM_STEP)
      this.draw()
    },
    zoomOut () {
      this.zoomLevel = Math.max(ZOOM_MIN, this.zoomLevel / ZOOM_STEP)
      this.draw()
    },
    handleResize () {
      this.draw()
    },
    setupResize () {
      if (this.resizeObserver) return

      const canvas = this.$refs.canvas
      if (!canvas) {
        this.$nextTick(() => this.setupResize())
        return
      }
      if (typeof ResizeObserver !== 'undefined') {
        this.resizeObserver = new ResizeObserver(() => this.draw())
        this.resizeObserver.observe(canvas.parentElement)
      } else {
        window.addEventListener('resize', this.handleResize)
      }
    },
  },
}
</script>

<style scoped>
.atlas-system-root {
  max-width: 800px;
  margin: 0 auto;
}

.canvas-wrapper {
  position: relative;
  width: 100%;
  aspect-ratio: 16 / 9;
  background: #0a0e17;
  border-radius: 6px;
  overflow: hidden;
}

.solar-canvas {
  display: block;
  width: 100%;
  height: 100%;
}

.zoom-controls {
  position: absolute;
  top: 0.5rem;
  right: 0.5rem;
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.legend {
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
  margin-top: 0.75rem;
  color: #363636;
}

.legend-item {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
}

.dot {
  display: inline-block;
  width: 10px;
  height: 10px;
  border-radius: 50%;
}

.star-dot {
  background: #fff4ea;
}

.planet-dot {
  background: #4a90d9;
}

.ship-dot {
  background: #e91e63;
  border-radius: 2px;
}

.player-dot {
  background: transparent;
  box-shadow: 0 0 6px #ffeb3b;
  border: 2px solid #ffeb3b;
}
</style>
